// src/pages/HomePage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import logo from '../assets/noo.png';
import backgroundImage from '../assets/home.jfif';

import {
  FaInstagram,
  FaLinkedin,
  FaFacebook,
  FaWhatsapp,
  FaBars,
  FaTimes,
  FaCog,
  FaSignOutAlt,
  FaUserMd,
  FaUserPlus,
  FaCalendarCheck,
  FaBrain,
  FaHeartbeat,
  FaTachometerAlt,
  FaFileInvoiceDollar,
  FaVideo,
  FaPrescriptionBottleAlt,
  FaBoxes,
  FaShieldAlt,
  FaClock,
  FaStethoscope,
} from 'react-icons/fa';

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [, setUserId] = useState(null);

  useEffect(() => {
    const storedRole = localStorage.getItem('userRole');
    const storedId = localStorage.getItem('userId');

    if (storedRole) {
      setUserRole(storedRole);
      setUserId(storedId);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const toggleNavbar = () => {
    setIsNavbarOpen(!isNavbarOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    setUserRole(null);
    setUserId(null);
    navigate('/login');
  };

  const navLinks = [
    { name: 'Services', path: '/home' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  // Change Password now lives in the nav bar, not in the action grid.
  const actionButtons = [
    { name: 'Find Patient Record', path: '/search-patient', roles: ['admin', 'doctor'], accent: 'var(--teal)', icon: FaUserMd },
    { name: 'Add New Patient', path: '/register-patient', roles: ['admin', 'doctor'], accent: 'var(--teal)', icon: FaUserPlus },
    { name: 'Manage Appointments', path: '/appointments', roles: ['admin', 'doctor'], accent: 'var(--coral)', icon: FaCalendarCheck },
    { name: 'Diagnosis Model', path: '/diagnosis-model', roles: ['admin', 'doctor'], accent: 'var(--teal)', icon: FaBrain },
    { name: 'Patient Portal', path: '/patient-portal', roles: ['patient'], accent: 'var(--coral)', icon: FaHeartbeat },
    { name: 'Dashboard', path: '/dashboard', roles: ['admin', 'doctor'], accent: 'var(--teal)', icon: FaTachometerAlt },
    { name: 'Billing System', path: '/billing', roles: ['admin', 'doctor'], accent: 'var(--coral)', icon: FaFileInvoiceDollar },
    { name: 'Telemedicine', path: '/telemedicine', roles: ['doctor', 'patient'], accent: 'var(--coral)', icon: FaVideo },
    { name: 'Prescriptions', path: '/prescriptions', roles: ['admin', 'doctor'], accent: 'var(--teal)', icon: FaPrescriptionBottleAlt },
    { name: 'Inventory', path: '/inventory', roles: ['admin'], accent: 'var(--ink)', icon: FaBoxes },
  ];

  const trustPoints = [
    { icon: FaStethoscope, label: 'Board-certified specialists' },
    { icon: FaClock, label: '24/7 telemedicine coverage' },
    { icon: FaShieldAlt, label: 'HIPAA-grade data privacy' },
  ];

  return (
    <div
      className="relative min-h-screen"
      style={{ fontFamily: 'var(--font-body)' }}
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center -z-20"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white/90 via-white/60 to-white/90" />

      {/* Navigation */}
      <nav className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Drogastro" className="h-10 rounded-lg" />
          </div>

          {/* Desktop links */}
          <div
            className="hidden md:flex items-center gap-1"
            style={{ fontFamily: 'var(--font-mono)' }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-xs tracking-[0.1em] uppercase font-medium px-4 py-2 rounded-full transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#2563EB] bg-blue-50'
                    : 'text-slate-600 hover:text-[#2563EB]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop right actions */}
          <div className="hidden md:flex items-center gap-2">
            {userRole && (
              <Link
                to="/change-password"
                title="Change Password"
                className="flex items-center gap-2 text-xs tracking-[0.08em] uppercase font-medium px-3 py-2 rounded-full text-slate-600 hover:text-[#2563EB] hover:bg-blue-50 transition-colors duration-200"
              >
                <FaCog className="text-sm" />
                <span className="hidden lg:inline">Change Password</span>
              </Link>
            )}

            {userRole ? (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-xs tracking-[0.08em] uppercase font-medium px-4 py-2 rounded-full border border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white transition-colors duration-200"
              >
                <FaSignOutAlt className="text-sm" />
                Logout
              </button>
            ) : (
              <Link
                to="/login"
                className="text-xs tracking-[0.08em] uppercase font-medium px-4 py-2 rounded-full bg-[#2563EB] text-white hover:bg-blue-700 transition-colors duration-200"
              >
                Log in
              </Link>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden bg-blue-50 rounded-full p-2 focus:outline-none"
            onClick={toggleNavbar}
          >
            {isNavbarOpen ? (
              <FaTimes className="text-lg text-[#2563EB]" />
            ) : (
              <FaBars className="text-lg text-[#2563EB]" />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {isNavbarOpen && (
          <div
            className="md:hidden flex flex-col border-t border-slate-200 bg-white px-4 py-3 gap-1"
            style={{ fontFamily: 'var(--font-mono)' }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsNavbarOpen(false)}
                className={`text-xs tracking-[0.1em] uppercase font-medium px-4 py-3 rounded-lg ${
                  location.pathname === link.path
                    ? 'text-[#2563EB] bg-blue-50'
                    : 'text-slate-600'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {userRole && (
              <Link
                to="/change-password"
                onClick={() => setIsNavbarOpen(false)}
                className="flex items-center gap-2 text-xs tracking-[0.1em] uppercase font-medium px-4 py-3 rounded-lg text-slate-600"
              >
                <FaCog className="text-sm" /> Change Password
              </Link>
            )}

            {userRole ? (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-xs tracking-[0.1em] uppercase font-medium px-4 py-3 rounded-lg text-[#2563EB] bg-blue-50 mt-1"
              >
                <FaSignOutAlt className="text-sm" /> Logout ({userRole})
              </button>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsNavbarOpen(false)}
                className="text-xs tracking-[0.1em] uppercase font-medium px-4 py-3 rounded-lg bg-[#2563EB] text-white mt-1 text-center"
              >
                Log in
              </Link>
            )}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center flex-grow pt-16 pb-24 px-4">
        <div className="text-center mb-8 max-w-2xl">
          <span
            className="block text-xs tracking-[0.25em] uppercase mb-3"
            style={{ fontFamily: 'var(--font-mono)', color: '#2563EB' }}
          >
            Patient &amp; Clinical Workspace
          </span>

          <h1
            className="text-4xl md:text-6xl font-medium leading-tight"
            style={{
              fontFamily: 'var(--font-display)',
              color: '#0F3B66',
              textShadow: '0 2px 10px rgba(255,255,255,0.6)',
            }}
          >
            Care, coordinated in one place
          </h1>

          <p className="mt-4 text-sm md:text-base text-slate-600">
            Everything your team needs to manage records, appointments and
            diagnoses — built around the patient, not the paperwork.
          </p>
        </div>

        {/* Trust bar */}
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-12 text-slate-600 text-xs md:text-sm">
          {trustPoints.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2">
              <Icon className="text-[#2563EB]" />
              <span>{label}</span>
            </div>
          ))}
        </div>

        {/* Action grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full max-w-6xl">
          {userRole &&
            actionButtons.map(
              (button) =>
                button.roles.includes(userRole) && (
                  <Link
                    key={button.name}
                    to={button.path}
                    className="group bg-white/95 hover:bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 p-5 flex items-center gap-4"
                  >
                    <span
                      className="flex items-center justify-center h-11 w-11 rounded-xl shrink-0 text-white text-lg"
                      style={{ backgroundColor: button.accent }}
                    >
                      <button.icon />
                    </span>
                    <span className="font-medium text-[var(--ink)] leading-snug">
                      {button.name}
                    </span>
                  </Link>
                )
            )}
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full p-4 flex flex-col md:flex-row items-center justify-between text-slate-500 text-sm bg-white/80 backdrop-blur-sm border-t border-slate-200">
        <p className="mb-2 md:mb-0">
          © 2023 Drogastro. All rights reserved
        </p>

        <div className="flex space-x-4">
          <FaInstagram className="text-xl hover:text-[#2563EB] cursor-pointer transition-colors duration-200" />
          <FaLinkedin className="text-xl hover:text-[#2563EB] cursor-pointer transition-colors duration-200" />
          <FaFacebook className="text-xl hover:text-[#2563EB] cursor-pointer transition-colors duration-200" />
          <FaWhatsapp className="text-xl hover:text-[#2563EB] cursor-pointer transition-colors duration-200" />
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
